import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import NavigationHeader from "../../components/ui/NavigationHeader";
import MainNavigation from "../../components/ui/MainNavigation";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import QuickActionPanel from "../../components/ui/QuickActionPanel";
import OrderFilters from "./components/OrderFilters";
import OrderToolbar from "./components/OrderToolbar";
import OrderTable from "./components/OrderTable";
import OrderPagination from "./components/OrderPagination";
import CreateOrderModal from "./components/CreateOrderModal";
import { useDispatch, useSelector } from "react-redux";
import { getOrdersService } from "redux/services/orders_service";
import { useTranslation } from "react-i18next";

const OrderManagement = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { metaOrders, ordersList } = useSelector((state) => state.orders);

  const [orders, setOrders] = useState([]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "orderDate",
    direction: "desc",
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    dateRange: "",
    customer: "",
    paymentMethod: "",
  });

  // Mock user data
  const user = {
    name: "Admin User",
    email: "admin@crm.com",
  };

  // Check for create action in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("action") === "create") {
      setShowCreateModal(true);
    }
  }, [location]);

  // Filter and sort orders
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = ordersList.filter((order) => {
      const matchesSearch =
        !filters.search ||
        order.orderNumber
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        order.customerName
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        order.customerEmail
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        order.items.some((item) =>
          item.name.toLowerCase().includes(filters.search.toLowerCase())
        );

      const matchesStatus = !filters.status || order.status === filters.status;
      const matchesPaymentMethod =
        !filters.paymentMethod ||
        order.paymentMethod.toLowerCase().replace(" ", "_") ===
          filters.paymentMethod;

      // Date range filtering
      let matchesDateRange = true;
      if (filters.dateRange) {
        const orderDate = new Date(order.orderDate);
        const now = new Date();

        switch (filters.dateRange) {
          case "today":
            matchesDateRange = orderDate.toDateString() === now.toDateString();
            break;
          case "week":
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDateRange = orderDate >= weekAgo;
            break;
          case "month":
            const monthAgo = new Date(
              now.getFullYear(),
              now.getMonth() - 1,
              now.getDate()
            );
            matchesDateRange = orderDate >= monthAgo;
            break;
          case "quarter":
            const quarterAgo = new Date(
              now.getFullYear(),
              now.getMonth() - 3,
              now.getDate()
            );
            matchesDateRange = orderDate >= quarterAgo;
            break;
          case "year":
            const yearAgo = new Date(
              now.getFullYear() - 1,
              now.getMonth(),
              now.getDate()
            );
            matchesDateRange = orderDate >= yearAgo;
            break;
          default:
            matchesDateRange = true;
        }
      }

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPaymentMethod &&
        matchesDateRange
      );
    });

    // Sort orders
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "orderDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [ordersList, filters, sortConfig]);

  // Pagination
  const totalItems = metaOrders?.total;
  const totalPages = metaOrders?.last_page;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredAndSortedOrders.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    dispatch(getOrdersService({ page: 1 })).then((response) => {
      if (response.payload?.status == 200) {
        setOrders(response.payload?.data?.data);
      }
    });
    setCurrentPage(1);
  }, [filters]);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSort = (sortConfig) => {
    setSortConfig(sortConfig);
  };

  const handleOrderSelect = (orderId, isSelected) => {
    if (isSelected) {
      setSelectedOrders([...selectedOrders, orderId]);
    } else {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOrders(paginatedOrders.map((order) => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleBulkAction = (action, orderIds) => {
    console.log("Bulk action:", action, "for orders:", orderIds);
    // Here you would implement the bulk action logic
    setSelectedOrders([]);
  };

  const handleExport = (format) => {
    console.log("Exporting orders as:", format);
    // Here you would implement the export logic
  };

  const handleCreateOrder = (newOrder) => {
    setOrders([newOrder, ...orders]);
    console.log("New order created:", newOrder);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getOrdersService({ page: page })).then((response) => {
      if (response.payload.status == 200) {
        setOrders(response.payload?.data?.data);
      }
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* 
      <NavigationHeader
        user={user}
        onMenuToggle={handleToggleSidebar}
        sidebarCollapsed={sidebarCollapsed}
      />

    
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleToggleSidebar}
      />*/}

      {/* Breadcrumb Navigation 
        <BreadcrumbNavigation />*/}

      {/* Page Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("order_list")}
              </h1>
              <p className="text-muted-foreground">
                {t("orderManagementDesc")}
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <div className="text-sm text-muted-foreground">
                {t("totalOrders")}:{" "}
                <span className="font-medium text-foreground">
                  {metaOrders?.total}
                </span>
              </div>
            </div>
          </div>

          {/* Order Filters
          <OrderFilters
            onFiltersChange={handleFiltersChange}
            totalOrders={orders.length}
            filteredOrders={filteredAndSortedOrders.length}
          /> */}

          {/* Order Toolbar
          <OrderToolbar
            selectedOrders={selectedOrders}
            onBulkAction={handleBulkAction}
            onCreateOrder={() => setShowCreateModal(true)}
            onExport={handleExport}
          /> */}

          {/* Order Table */}
          <OrderTable
            orders={ordersList}
            selectedOrders={selectedOrders}
            onOrderSelect={handleOrderSelect}
            onSelectAll={handleSelectAll}
            sortConfig={sortConfig}
            onSort={handleSort}
          />

          {/* Pagination */}
          <OrderPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Create Order Modal */}
      {showCreateModal && (
        <CreateOrderModal
          onClose={() => setShowCreateModal(false)}
          onCreateOrder={handleCreateOrder}
        />
      )}
    </div>
  );
};

export default OrderManagement;
